export const FORM_LIMITS = { count: 12, name: 80, size: 50 };
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isFormId = (v) => typeof v === 'string' && /^[A-Za-z0-9-]{1,40}$/.test(v);
const isFormSize = (v) => isObj(v) && typeof v.w === 'number' && typeof v.h === 'number'
    && Number.isFinite(v.w) && Number.isFinite(v.h) && v.w > 0 && v.h > 0 && v.w <= FORM_LIMITS.size && v.h <= FORM_LIMITS.size;
const isFormName = (v) => typeof v === 'string' && v.trim().length >= 1 && v.length <= FORM_LIMITS.name && !/[\u0000-\u001f\u007f]/.test(v);
/** A whole list of forms, or null when any of it is malformed. Ids are unique within the token. */
export function parseForms(v) {
    if (!Array.isArray(v) || v.length > FORM_LIMITS.count)
        return null;
    const out = [];
    for (const f of v) {
        if (!isObj(f) || !isFormId(f.id) || !isFormName(f.name) || typeof f.asset !== 'string' || !UUID.test(f.asset))
            return null;
        if (f.size !== undefined && !isFormSize(f.size))
            return null;
        if (out.some((x) => x.id === f.id))
            return null;
        out.push({ id: f.id, name: f.name.trim(), asset: f.asset, ...(f.size ? { size: { w: f.size.w, h: f.size.h } } : {}) });
    }
    return out;
}
export function parseWearing(v) {
    if (!isObj(v) || !isFormId(v.form) || !isFormSize(v.size))
        return null;
    if (v.asset !== null && (typeof v.asset !== 'string' || !UUID.test(v.asset)))
        return null;
    return { form: v.form, asset: v.asset, size: { w: v.size.w, h: v.size.h } };
}
export const copyForms = (forms) => forms.map((f) => ({ ...f, ...(f.size ? { size: { ...f.size } } : {}) }));
export const copyWearing = (w) => ({ ...w, size: { ...w.size } });
/** Every picture a token's forms name, its true form's among them: the DM's to read (`assets.ts`). */
export function formPictures(o) {
    const ids = new Set((o.forms ?? []).map((f) => f.asset));
    if (o.wearing?.asset)
        ids.add(o.wearing.asset);
    return ids;
}
/**
 * The token in `form` (null: its true form), its top-left square kept. `pitch` is the grid's square in board pixels on
 * each axis. Unchanged (the same object) when there is nothing to do: an unknown form, or changing back from none.
 */
export function wearForm(o, form, pitch) {
    const base = o.wearing ? { asset: o.wearing.asset, size: o.wearing.size } : { asset: o.asset, size: o.size };
    const f = form === null ? null : (o.forms ?? []).find((x) => x.id === form);
    if (form !== null && !f)
        return o;
    if (form === null && !o.wearing)
        return o;
    const size = f ? (f.size ?? base.size) : base.size;
    const { wearing: _w, ...rest } = o;
    return {
        ...rest,
        asset: f ? f.asset : base.asset,
        size: { ...size },
        x: o.x + ((size.w - o.size.w) * pitch.x) / 2,
        y: o.y + ((size.h - o.size.h) * pitch.y) / 2,
        ...(f ? { wearing: { form: f.id, asset: base.asset, size: { ...base.size } } } : {}),
        rev: o.rev + 1,
    };
}
/**
 * New forms for a token. A worn form that is gone is changed back from; a worn form still listed is worn afresh, so
 * an edit to its picture or size shows at once.
 */
export function setForms(o, forms, pitch) {
    const { forms: _f, ...rest } = o;
    const next = (forms.length ? { ...rest, forms: copyForms(forms), rev: o.rev + 1 } : { ...rest, rev: o.rev + 1 });
    if (!o.wearing)
        return next;
    const back = wearForm(next, null, pitch);
    return forms.some((f) => f.id === o.wearing.form) ? wearForm(back, o.wearing.form, pitch) : back;
}
export function parseGrow(v) {
    if (!isObj(v) || typeof v.steps !== 'number' || !Number.isInteger(v.steps) || v.steps === 0 || Math.abs(v.steps) > 4 || !isFormSize(v.from) || !isFormSize(v.to))
        return null;
    return { steps: v.steps, from: { w: v.from.w, h: v.from.h }, to: { w: v.to.w, h: v.to.h } };
}
/**
 * The squares a token of `from` takes `steps` sizes larger (smaller below 0) by the system's ladder (`combat.reach.sizes`,
 * smallest first): its size read from its squares as `reachBySize` reads it (one square is Medium), never under one
 * square on the board. Null when the ladder has no such size.
 */
export function grownSize(ladder, from, steps) {
    const across = Math.max(from.w, from.h);
    let at = 0;
    ladder.forEach((s, i) => { if (s.squares <= across + 1e-9)
        at = i; });
    const next = ladder[at + steps];
    if (!next)
        return null;
    const n = Math.min(FORM_LIMITS.size, Math.max(1, next.squares));
    return { w: n, h: n };
}
//# sourceMappingURL=forms.js.map